import EventEmitter from 'events';
import {
    VIEW_MODE_DESIGNER,
    VIEW_MODE_MANAGE_DASHBOARD,
    VIEW_MODE_PAS,
} from '../../constants/global-constants';
import {
    MILLISECONDS_TO_DELAY_NOTIFICATION_CLOSE,
    NOTIFICATION_TYPE_ERROR,
    NOTIFICATION_TYPE_SUCCESS,
} from '../../constants/notifications-constants';
import cookieHelper from '../../helpers/cookie-helper';
import responsiveHelper from '../../helpers/responsive-helper';
import ticketHelper from '../../helpers/ticket-helper';
import timestamp from '../../helpers/timestamp';
import assetService from '../../services/asset-service';
import cartServiceFactory from '../../services/cart-service';
import eventServiceFactory from '../../services/event-service';
import manageDashboardManagerFactory from '../manage-dashboard-manager-factory';
import pasManagerFactory from '../pas-manager-factory';
import pdfManagerFactory from '../pdf-manager-factory';
import seatmapManagerFactory from '../seatmap-manager-factory';
import orderTokenHelperFactory from './order-token-helper';

const GUEST_TOKEN_PATH = 'tokens.guest_token';

const eventManagerFactory = ({ director, model }) => {
    const {
        get: directorGet,
        getAppType: directorGetAppType,
        getEventId: directorGetEventId,
        getInventoryService,
        getIsLoading,
        getPageParams,
        getServerProperty,
        on: directorOn,
        set: directorSet,
        setLoading,
        setPageParam,
        setView,
        trackAnalyticsAction,
    } = director;

    const { getVariantsFromTier } = ticketHelper;

    const { aGetAsset } = assetService;

    const { getTicketPositionNormalizedCoordinates } = responsiveHelper;

    const { getCookie } = cookieHelper;

    const _cartService = cartServiceFactory();
    const _orderTokenHelper = orderTokenHelperFactory({
        cartService: _cartService,
        get: directorGet,
        getPageParams,
        getServerProperty,
        set: directorSet,
    });
    const _eventEmitter = new EventEmitter();
    const _designerManager = undefined;

    let _seatmapManager = undefined;
    let _pasManager = undefined;
    let _manageDashboardManager = undefined;
    let _pdfManager = undefined;

    const getOrderHeaders = ({ excludeOrderToken } = {}) => {
        const csrftoken = getCookie('csrftoken');
        let guestToken;

        const orderTokenToUse = _orderTokenHelper.getOrderToken({
            get: model.get,
        });
        // We will not have a guest token if we have a csrftoken

        if (!csrftoken) {
            guestToken = directorGet(GUEST_TOKEN_PATH);
        }

        const headers = {
            'X-CSRFToken': csrftoken,
            'X-Requested-With': 'XMLHttpRequest',
            'content-type': 'application/json',
            ...(guestToken ? { 'X-Guest-Token': guestToken } : {}),
            ...(orderTokenToUse && !excludeOrderToken
                ? { 'X-ORDER-TOKEN': orderTokenToUse }
                : {}),
        };

        return headers;
    };

    const _eventService = eventServiceFactory({ getOrderHeaders });

    const isErrorCode = (code) => {
        const numericCode = +code;

        if (numericCode && numericCode >= 400) {
            return true;
        }
        return false;
    };

    const getViewMode = () => model.get('globals.viewMode');

    const setViewMode = ({ mode }) => model.set('globals.viewMode', mode);

    const setOrderData = (orderData = {}) => {
        // We set the current timestamp in the model so that we can calculate how much time we have left
        // If we did not do this, we would not have a value to compare the expiration_
        const orderDataToUse = {
            ...orderData,
            ts: Date.now(),
        };

        model.set('globals.order', orderDataToUse);
    };

    const aRetrieveTickets = async ({
        eventId,
        holdId,
        promoCode,
        includeHidden,
    }) => {
        const { pos } = getPageParams();
        const { ticketClasses } = await _eventService.aRetrieveTickets({
            eventId,
            promoCode,
            pos,
            holdId,
            includeHidden,
        });
        let ticketClassesModel = {};

        if (ticketClasses && ticketClasses.ticket_classes) {
            ticketClassesModel = ticketClasses.ticket_classes;
        }

        return {
            ticketClassesModel,
        };
    };

    const aRetrieveStatus = async ({
        eventId,
        holdId,
        promoCode,
        viewMode,
    }) => {
        const { holdStatus, mapStatus } = await _eventService.aRetrieveStatus({
            eventId,
            promoCode,
            holdId,
            viewMode,
        });
        let holdStatusToUse = holdStatus;

        if (holdStatus && isErrorCode(holdStatus['status_code'])) {
            holdStatusToUse = null;
        }

        const statusModel = _seatmapManager.parseStatus({
            holdStatus: holdStatusToUse,
            mapStatus,
        });

        let usingHold = false;

        if (holdStatusToUse) {
            usingHold = true;
        }
        return {
            statusModel,
            usingHold,
        };
    };

    const aRetrieveEventData = async ({ eventId }) => {
        const eventInfo = await _eventService.aRetrieveEventData({
            eventId,
        });

        return eventInfo;
    };

    const aShowTicketSelection = ({ ids, visible }) => {
        if (!_seatmapManager) {
            return Promise.resolve();
        }
        return _seatmapManager.aShowTicketSelection({ ids, visible });
    };

    const gotoSection = (id) => {
        _seatmapManager.aZoomToObject(id);
    };

    const aEnsureOrderToken = async () => {
        await _orderTokenHelper.aEnsureOrderToken();
    };

    const aEnsureGuestTokenIfNeeded = async ({ eventId }) => {
        const csrftokenInCookie = getCookie('csrftoken');

        if (csrftokenInCookie) {
            return;
        }

        let guestToken = directorGet(GUEST_TOKEN_PATH);

        if (guestToken) {
            return;
        }
        console.log('ensuring guestToken');
        guestToken = await _eventService.aGetGuestToken({ eventId });
        console.log('guest token obtained');
        directorSet(GUEST_TOKEN_PATH, guestToken);
    };

    const aEnsureCartIdIfNeeded = async ({ cartId, eventId, viewMode }) => {
        if (cartId) {
            return cartId;
        }
        if (viewMode === VIEW_MODE_MANAGE_DASHBOARD) {
            const { body: orderCreationResult, headers } =
                await _eventService.aCreateOrder({ manual: true, eventId });

            const orderToken = headers['x-order-token'];

            _orderTokenHelper.setOrderToken(orderToken);

            const { id } = orderCreationResult;

            if (!id) {
                throw new Error('Cannot create cart');
            }
            setPageParam({ key: 'cartId', value: id });

            await _setOrderToManual();

            return id;
        }
        return null;
    };

    const _setOrderToManual = async () => {
        // Order service requries us to set the payment method so that order is set to manual
        // and we're able to change ticket prices
        await _pasManager.setPaymentInstruction({ paymentStatus: 'check' });
    };

    const aFetchEventFromInventoryService = async ({ eventId }) => {
        const inventoryService = getInventoryService
            ? getInventoryService()
            : null;

        if (!inventoryService) {
            throw new Error('Inventory service not defined');
        }
        model.set(null, {});
        setLoading({ scope: 'event', value: true });

        const ts = timestamp();
        const promiseArray = [inventoryService.aFetchEvent({ eventId })];

        const results = await Promise.all(promiseArray);
        const { error, seatmap, status } = results[0] || {};

        let seatmapModel;

        let tiersModel;
        let statusModel = {};

        try {
            const parseResult = await _seatmapManager.parseSeatmapAndHolds({
                seatmap,
            });

            seatmapModel = parseResult.seatmapModel;
            tiersModel = _seatmapManager.parseTiers({
                tierColors: seatmap && seatmap['tier_colors'],
            });
            statusModel = _seatmapManager.parseStatus({
                mapStatus: status,
            });
        } catch (e) {
            seatmapModel = {};
            tiersModel = {};
        }

        model.set(null, seatmapModel);
        model.set('tiers', tiersModel);
        model.set('status', statusModel);
        model.set('error', error);

        model.set('globals.statusTs', ts);

        setLoading({ scope: 'event', value: false });
    };

    // eslint-disable-next-line complexity
    const aInit = async ({
        cartId,
        complimentaryOrder,
        deliveryMethodOrder,
        eventId,
        fetchTicketClasses = true,
        holdId,
        promoCode,
        viewMode,
    } = {}) => {
        model.set(null, {});
        // Show spinning wheel as scope event now is being loaded
        setLoading({ scope: 'event', value: true });

        // We make sure that we have the order token and the guest token

        await aEnsureOrderToken();
        await aEnsureGuestTokenIfNeeded({ eventId });
        const cartIdToUse = await aEnsureCartIdIfNeeded({
            cartId,
            eventId,
            viewMode,
        });

        const ts = timestamp();
        const promiseArray = [
            _eventService.aFetchEvent({
                cartId: cartIdToUse,
                eventId,
                includeGroups: viewMode !== VIEW_MODE_PAS,
                includeTierColors: viewMode !== VIEW_MODE_MANAGE_DASHBOARD,
            }),
            aRetrieveStatus({ eventId, holdId, promoCode, viewMode }),
        ];

        if (fetchTicketClasses) {
            promiseArray.push(
                aRetrieveTickets({
                    eventId,
                    holdId,
                    promoCode,
                    includeHidden: viewMode === VIEW_MODE_MANAGE_DASHBOARD,
                }),
            );
        }

        if (viewMode === VIEW_MODE_MANAGE_DASHBOARD) {
            promiseArray.push(aRetrieveEventData({ eventId }));
            promiseArray.push(
                _eventService.aRetrieveInventoryTiers({
                    eventId,
                }),
            );
        }

        const results = await Promise.all(promiseArray);
        const { attendees, order, seatmap } = results[0] || {};
        const { statusModel, usingHold } = results[1] || {};

        const { ticketClassesModel } = results[2] || {};
        const eventData = results[3] || {};
        const tiersInfo = results[4] || [];
        const tierColors = (seatmap && seatmap['tier_colors']) || {};

        let seatmapModel;

        let tiersModel;
        let attendeesModel = {};
        let holds = {};

        if (attendees && attendees.attendees) {
            attendeesModel = attendees.attendees;
        }

        try {
            const { mapStatus } = await _eventService.aRetrieveStatus({
                eventId,
                promoCode,
                holdId,
                viewMode,
            });
            const parseResult = await _seatmapManager.parseSeatmapAndHolds({
                seatmap,
                mapStatus,
            });

            seatmapModel = parseResult.seatmapModel;
            holds = parseResult.holds;
            tiersModel = _seatmapManager.parseTiers({
                tiersInfo,
                tierColors,
            });
        } catch (e) {
            seatmapModel = {};
            tiersModel = {};
            holds = {};
        }

        // It could be that the tiers come in the venuemap or in the status
        // We expect them to be in the seatmap after refactoring on Dec 2019
        // so if they are not in the seatmap (old saved maps), we have to
        // retrieve them from the status and merge them in the map
        _seatmapManager.mergeTiersFromStatusToMap({
            seatmapModel,
            statusModel,
        });
        model.set(null, seatmapModel);
        model.set('tiers', tiersModel);
        model.set('status', statusModel);
        model.set('holds', holds);

        if (ticketClassesModel) {
            model.set('globals.ticketClasses', ticketClassesModel);
        }
        model.set('globals.promoCode', promoCode);
        model.set('globals.usingHold', usingHold);
        model.set('globals.statusTs', ts);
        model.set('globals.complimentaryOrder', complimentaryOrder);
        model.set('globals.deliveryMethodOrder', deliveryMethodOrder);
        model.set('globals.holdId', holdId);
        model.set('globals.eventData', eventData);

        setViewMode({ mode: viewMode });
        switch (viewMode) {
            case VIEW_MODE_MANAGE_DASHBOARD:
                if (!_manageDashboardManager) {
                    _manageDashboardManager = manageDashboardManagerFactory({
                        eventId,
                        model,
                    });
                    await _manageDashboardManager.aInit();
                }
                trackAnalyticsAction({ action: 'ManageDashboardLoadComplete' });
            // eslint-disable-next-line no-fallthrough
            case VIEW_MODE_PAS:
                if (order) {
                    setOrderData(order);
                }
                _pasManager.setCartFromAttendees({
                    attendeesNode: attendeesModel,
                });
                _pasManager.notifyAppStarted();
                trackAnalyticsAction({ action: 'ReactPickASeatLoadComplete' });
                await _pasManager.aLookAtAttendees({
                    attendeesNode: attendeesModel,
                });

                break;
            // TODO get the instance from
            // import designerManagerFactory from '../designer/designer-manager-factory';
            // This has been removed from the webapp and should be imported from its own lib
            // case VIEW_MODE_DESIGNER:
            //     if (!_designerManager) {
            //         _designerManager = designerManagerFactory({eventId, model});
            //         await _designerManager.aInit();
            //     }
            //     break;

            default:
        }

        setLoading({ scope: 'event', value: false });
    };

    const on = (eventName, handler) => {
        _eventEmitter.on(eventName, handler);
    };

    const off = (eventName, handler) => {
        _eventEmitter.off(eventName, handler);
    };

    directorOn('change', () => {
        _eventEmitter.emit('change');
    });

    const seatmapMouseDown = ({ intersects, isTouch, keys, pos }) => {
        _seatmapManager.seatmapMouseDown({ intersects, isTouch, keys, pos });
    };

    const getChairRadiusInPixels = () => {
        const viewBox = _seatmapManager.getViewBox();
        const viewport = _seatmapManager.getViewport();

        if (viewBox && viewport) {
            return _seatmapManager.getChairRadiusInPixels({
                viewBox,
                viewport,
            });
        }
        return 0;
    };

    const seatmapGetViewport = () => _seatmapManager.getViewport();

    const seatmapSetViewport = (viewport) => {
        _seatmapManager.setViewport(viewport);
    };

    const seatmapMouseMove = ({ intersects, isTouch, keys, pos }) => {
        _seatmapManager.seatmapMouseMove({ intersects, isTouch, keys, pos });
    };

    const seatmapMouseUp = () => {
        _seatmapManager.seatmapMouseUp();
    };

    const seatmapWheel = ({ delta, isTouch, pos }) => {
        _seatmapManager.seatmapWheel({ delta, isTouch, pos });
    };

    const pickSeats = ({ selectedSeats, value }) => {
        _pasManager.pickSeats({ selectedSeats, value });
    };

    const _getEventIdAndCartId = () => {
        const { cartId, eventId } = getPageParams();

        return {
            cartId,
            eventId,
        };
    };

    const _getEventId = () => {
        const { eventId } = getPageParams();

        return eventId;
    };

    const aSetPromoCode = async ({ promoCode }) => {
        const eventId = _getEventId();

        const statusTs = timestamp();
        const { statusModel, usingHold } = await aRetrieveStatus({
            eventId,
            promoCode,
        });

        const { ticketClassesModel } = await aRetrieveTickets({
            eventId,
            promoCode,
        });

        model.set('status', statusModel);
        model.set('globals.ticketClasses', ticketClassesModel);
        model.set('globals.promoCode', promoCode);
        model.set('globals.usingHold', usingHold);
        model.set('globals.statusTs', statusTs);
    };

    const isSeatLocked = ({ id }) => _pasManager.isSeatLocked({ id });

    const getEventId = () => directorGetEventId();

    const getSeatmapViewBox = () => _seatmapManager.getViewBox();

    const _zoomButtons = {
        zoomIn: {
            enabled: true,
            handler: () => _seatmapManager.zoomIn(),
        },
        zoomOut: {
            enabled: true,
            handler: () => _seatmapManager.zoomOut(),
        },
        zoomToFit: {
            enabled: false,
            handler: () => _seatmapManager.zoomToFit(),
        },
    };

    const getZoomButtons = () => {
        const viewMode = getViewMode();

        switch (viewMode) {
            case VIEW_MODE_DESIGNER:
            case VIEW_MODE_MANAGE_DASHBOARD:
                _zoomButtons.zoomToFit.enabled = true;
                break;
            default:
                _zoomButtons.zoomToFit.enabled = false;
        }

        return _zoomButtons;
    };

    const getSeatInfoFromIds = ({ ids }) =>
        _pasManager.getSeatInfoFromIds({ ids });

    const getTargetPositionInNormalizedCoordinates = () => {
        let position =
            _seatmapManager.getTargetPositionInNormalizedCoordinates() || {};

        if (!position.x) {
            const viewport = seatmapGetViewport();

            position = getTicketPositionNormalizedCoordinates({ viewport });
        }
        return position;
    };

    const getTierFromId = ({ id }) => _seatmapManager.getTierFromId({ id });

    const getTicketSelection = () => {
        const { ids, position, useArrow, visible } =
            _seatmapManager.getTicketSelectionInfo();

        if (!ids || !ids.length) {
            return {
                position,
                useArrow,
                visible,
            };
        }
        const firstId = ids[0];
        const tier = getTierFromId({ id: firstId });
        const globals = model.get('globals');
        const variants = getVariantsFromTier({
            globals,
            tier,
        });
        const hasHolds = _pasManager.seatsHaveHolds({ ids });
        const isSold = _pasManager.seatIsSold({ ids });

        if (isSold) {
            _pasManager.setSoldSeatInfo({ ids });
        }

        const seatInfo = getSeatInfoFromIds({ ids });

        return {
            hasHolds,
            ids,
            isSold,
            position,
            seatInfo: {
                ...seatInfo,
                tierColor: _pasManager.getColorFromTier(tier),
            },
            useArrow,
            variants,
            visible,
        };
    };

    const getPositionFromIds = ({ ids }) =>
        _seatmapManager.getPositionFromIds({ ids });

    const selectSeats = (seats) => {
        _seatmapManager.selectSeats(seats);
    };

    const getWorldToPixelsFactor = () => {
        const viewBox = _seatmapManager.getViewBox();
        const viewport = _seatmapManager.getViewport();

        if (viewBox && viewport) {
            return _seatmapManager.getWorldToPixelsFactor({
                viewBox,
                viewport,
            });
        }
        return 1;
    };

    const getIsAnimating = () => _seatmapManager.getIsAnimating();

    const getHoverSectionInfo = () => {
        if (!_manageDashboardManager) {
            return null;
        }
        const hoverSectionInfo = _manageDashboardManager.getHoverSectionInfo();

        if (!hoverSectionInfo) {
            return null;
        }

        const viewBox = _seatmapManager.getViewBox();
        const viewport = _seatmapManager.getViewport();

        const farView = _seatmapManager.seatsAreSmall({ viewBox, viewport });

        const { hoverSectionId } = hoverSectionInfo;

        const positionInWorldCoordinates = getPositionFromIds({
            ids: [hoverSectionId],
        });
        const positionNormalized = _seatmapManager.worldToNormalized(
            positionInWorldCoordinates,
        );

        return {
            ...hoverSectionInfo,
            farView,
            position: positionNormalized,
        };
    };

    const getOrderInfo = () => _pasManager.getOrderInfo();

    const removeCartItem = ({ seatId, variantId }) =>
        _pasManager.removeCartItem({ seatId, variantId });

    const aSyncCart = () => _pasManager.aSyncCart();

    const getSyncCartStatus = () => _pasManager.getSyncCartStatus();

    const aLookAt = ({
        ids,
        margin,
        targetPositionInNormalizedViewCoordinates,
        targetViewBox,
    }) =>
        _seatmapManager.aLookAt({
            ids,
            margin,
            targetPositionInNormalizedViewCoordinates,
            targetViewBox,
        });

    const getInteractive = () => {
        const viewMode = getViewMode();

        switch (viewMode) {
            case VIEW_MODE_DESIGNER:
                return _designerManager ? _designerManager.interactive : null;
            /*   case VIEW_MODE_MANAGE_DASHBOARD:
                return _manageDashboardManager ? _manageDashboardManager.interactive : null;*/
            default:
        }
        return null;
    };
    const init = () => {
        if (!_seatmapManager) {
            _seatmapManager = seatmapManagerFactory({
                aGetAsset,
                getAppType: directorGetAppType,
                getPageParams,
                getSeatInfoFromIds,
                getUpstreamInteractive: getInteractive,
                getViewMode,
                isSeatLocked,
                model,
                pickSeats,
                setErrorMessage,
                setViewMode,
            });
        }
        if (!_pasManager) {
            _pasManager = pasManagerFactory({
                aLookAt,
                aSetPromoCode,
                aShowTicketSelection,
                cartService: _cartService,
                eventService: _eventService,
                getEventIdAndCartId: _getEventIdAndCartId,
                getPageParams,
                getPositionFromIds,
                getTierFromId,
                getViewMode,
                getViewport: seatmapGetViewport,
                model,
                selectSeats,
                setErrorMessage,
                setOrderData,
                setSuccessMessage,
                trackAnalyticsAction,
            });
        }

        if (!_pdfManager) {
            _pdfManager = pdfManagerFactory({
                model,
            });
        }
    };

    const getSeatmapManager = ({ inJest }) => {
        // Do not call this function if not doing tests in jest
        // We need it in jest so that we can spy on function calls
        // but we should not call seatmapManager directly
        // from the outside.
        if (!inJest) {
            return null;
        }
        return _seatmapManager;
    };

    const getPasManager = ({ inJest }) => {
        //Do not call this function if not doing tests in jest
        // We need it in jest so that we can spy on function calls
        // but we should not call pasManager directly
        // from the outside.
        if (!inJest) {
            return null;
        }
        return _pasManager;
    };

    const getManageDashboardManager = ({ inJest }) => {
        //Do not call this function if not doing tests in jest
        // We need it in jest so that we can spy on function calls
        // but we should not call manageDashboardManager directly
        // from the outside.
        if (!inJest) {
            return null;
        }
        return _manageDashboardManager;
    };

    const getPDFManager = ({ inJest }) => {
        //Do not call this function if not doing tests in jest
        // We need it in jest so that we can spy on function calls
        // but we should not call PDFManager directly
        // from the outside.
        if (!inJest) {
            return null;
        }
        return _pdfManager;
    };

    const getEventService = ({ inJest }) => {
        //Do not call this function if not doing tests in jest
        // We need it in jest so that we can spy on function calls
        // but we should not call eventService directly
        // from the outside.
        if (!inJest) {
            return null;
        }
        return _eventService;
    };

    const setAddonQuantity = (id, value) => {
        _pasManager.setAddonQuantity(id, value);
    };

    const setOrderSummaryTicketPrice = (ticketClassId, value) => {
        _pasManager.setOrderSummaryTicketPrice(ticketClassId, value);
    };

    const get = (...args) => model.get(...args);

    const undo = () => (_designerManager ? _designerManager.undo() : null);
    const redo = () => (_designerManager ? _designerManager.redo() : null);

    const getNotifications = () => {
        const viewMode = getViewMode();

        switch (viewMode) {
            case VIEW_MODE_MANAGE_DASHBOARD:
                if (_manageDashboardManager) {
                    return _manageDashboardManager.getNotifications();
                }
                break;
            default:
        }
        return [];
    };

    const getEventData = () => model.get('globals.eventData');

    const getTotalUnitsInfo = () => _pasManager.getTotalUnitsInfo();

    const getGASectionVariants = ({ sectionId, variants }) =>
        _pasManager.getGASectionVariants({ sectionId, variants });

    const setSelectedGASectionVariantQuantity = (
        sectionId,
        variantId,
        quantity,
    ) =>
        _pasManager.setSelectedGASectionVariantQuantity(
            sectionId,
            variantId,
            quantity,
        );

    const resetSelectedGATickets = (isVisible) =>
        _pasManager.resetSelectedGATickets(isVisible);

    const handleSelectGATickets = ({ sectionId }) =>
        _pasManager.handleSelectGATickets({ sectionId });

    const hasSelectedGATickets = () => _pasManager.hasSelectedGATickets();

    const hasItemsCarted = () => _pasManager.hasItemsCarted();

    const getTiers = () => model.get('tiers');

    const getLegendDialogVisibility = () =>
        _pasManager.getLegendDialogVisibility();

    const toggleLegendDialogVisibility = () =>
        _pasManager.toggleLegendDialogVisibility();

    const hideLegendDialog = () => _pasManager.hideLegendDialog();

    const getCheckoutModalVisibility = () =>
        _pasManager.getCheckoutModalVisibility();

    const setCheckoutModalVisibility = (value) =>
        _pasManager.setCheckoutModalVisibility(value);

    const setPaymentInstruction = (value) =>
        _pasManager.setPaymentInstruction(value);

    const getPaymentInstruction = () => _pasManager.getPaymentInstruction();

    const getOrderPriceInfo = () => _pasManager.getOrderPriceInfo();

    const getManageHoldsModalVisibility = () =>
        _pasManager.getManageHoldsModalVisibility();

    const setManageHoldsModalVisibility = (value) =>
        _pasManager.setManageHoldsModalVisibility(value);

    const showManageHoldsModal = ({ sectionId }) =>
        _pasManager.showManageHoldsModal({ sectionId });

    const getSectionHoldsInfo = () => _pasManager.getSectionHoldsInfo();

    const _getNotification = ({ type }) =>
        model.get(`notifications.${type}`) || '';

    const _clearNotification = ({ type }) =>
        model.set(`notifications.${type}`, null);

    const _setNotification = ({ msg, type, isTransient = false }) => {
        model.set(`notifications.${type}`, msg.toString());

        if (isTransient) {
            setTimeout(
                () => _clearNotification({ type }),
                MILLISECONDS_TO_DELAY_NOTIFICATION_CLOSE,
            );
        }
    };

    const setErrorMessage = ({ msg, isTransient }) =>
        _setNotification({
            msg,
            isTransient,
            type: NOTIFICATION_TYPE_ERROR,
        });

    const getErrorMessage = () =>
        _getNotification({ type: NOTIFICATION_TYPE_ERROR });

    const clearErrorMessage = () =>
        _clearNotification({ type: NOTIFICATION_TYPE_ERROR });

    const setSuccessMessage = ({ msg, isTransient }) =>
        _setNotification({
            msg,
            isTransient,
            type: NOTIFICATION_TYPE_SUCCESS,
        });

    const getSuccessMessage = () =>
        _getNotification({ type: NOTIFICATION_TYPE_SUCCESS });

    const clearSuccessMessage = () =>
        _clearNotification({ type: NOTIFICATION_TYPE_SUCCESS });

    const idsBelongToGASection = ({ ids }) =>
        _pasManager.seatsBelongToGASection({ ids });

    const getSectionTotalUnitsInfo = (sectionId) => {
        let sectionTotalUnitsInfo;

        if (
            idsBelongToGASection({
                ids: [sectionId],
            })
        ) {
            sectionTotalUnitsInfo =
                _pasManager.getGASectionTotalUnitsInfo(sectionId);
        } else {
            sectionTotalUnitsInfo =
                _manageDashboardManager.getReservedSectionTotalUnitsInfo(
                    sectionId,
                );
        }

        return sectionTotalUnitsInfo;
    };

    const setSectionHoldQuantity = ({ holdId, quantity }) =>
        _pasManager.setSectionHoldQuantity({ holdId, quantity });

    const saveSectionHolds = async () => {
        const viewMode = getViewMode();
        const eventId = _getEventId();

        await _pasManager.saveSectionHolds();

        const { statusModel } = await aRetrieveStatus({
            eventId,
            viewMode,
        });

        model.set('status', statusModel);
    };

    const getSectionTiersData = (sectionId) => {
        const sectionTiersIds =
            _seatmapManager.getTiersFromSectionId(sectionId);
        const globals = model.get('globals');
        const tiers = getTiers();
        const sectionTiersData = [];

        sectionTiersIds.forEach((sectionTierId) => {
            const parsedSectionTierId = +sectionTierId;
            const sectionTier = tiers[parsedSectionTierId];

            if (sectionTier) {
                const { name, color, quantityAvailable, quantityTotal } =
                    sectionTier;
                const sectionTierData = {
                    id: parsedSectionTierId,
                    name,
                    color,
                    quantityAvailable,
                    quantityTotal,
                };
                const tierVariants = getVariantsFromTier({
                    globals,
                    tier: parsedSectionTierId,
                });

                tierVariants.forEach((tierVariant) => {
                    if (
                        !sectionTierData.price ||
                        tierVariant.price < sectionTierData.price
                    ) {
                        sectionTierData.price = tierVariant.price;
                    }
                });

                sectionTiersData.push(sectionTierData);
            }
        });

        return sectionTiersData;
    };

    const savePaymentInstruction = () => _pasManager.savePaymentInstruction();

    const getCartId = () => {
        const { cartId } = getPageParams();

        return cartId;
    };

    const getCheckoutWidgetVisibility = () =>
        _pasManager.getCheckoutWidgetVisibility();

    const setCheckoutWidgetVisibility = (value) =>
        _pasManager.setCheckoutWidgetVisibility(value);

    const getPrintDialogVisibility = () =>
        _pasManager.getPrintDialogVisibility();

    const togglePrintDialogVisibility = () =>
        _pasManager.togglePrintDialogVisibility();

    const hidePrintDialog = () => _pasManager.hidePrintDialog();

    const getPDF = (args) => _pdfManager.getPDF(args);

    const getPrintOptionValue = (printOptionId) =>
        _pdfManager.getPrintOptionValue(printOptionId);

    const setPrintOptionValue = ({ printOptionId, value }) =>
        _pdfManager.setPrintOptionValue({
            printOptionId,
            value,
        });

    const getSoldSeatInfo = () => _pasManager.getSoldSeatInfo();

    const getSoldSeatInfoFetched = () => _pasManager.getSoldSeatInfoFetched();

    const releaseHold = async ({ id }) => {
        const viewMode = getViewMode();
        const eventId = _getEventId();

        await _pasManager.releaseHold({ id });

        const { statusModel } = await aRetrieveStatus({
            eventId,
            viewMode,
        });

        model.set('status', statusModel);
    };

    const getHeldSeatIsReleasing = () => _pasManager.getHeldSeatIsReleasing();

    init();

    if (typeof window !== 'undefined') {
        window.debugUndo = undo;
        window.debugRedo = redo;
    }

    return {
        aEnsureOrderToken,
        aFetchEventFromInventoryService,
        aInit,
        aLookAt,
        aRetrieveEventData,
        aRetrieveStatus,
        aRetrieveTickets,
        aShowTicketSelection,
        aSyncCart,
        clearErrorMessage,
        clearSuccessMessage,
        get,
        getAppType: directorGetAppType,
        getCartId,
        getChairRadiusInPixels,
        getCheckoutModalVisibility,
        getCheckoutWidgetVisibility,
        getErrorMessage,
        getEventData,
        getEventId,
        getEventService,
        getHeldSeatIsReleasing,
        getIsAnimating,
        getIsLoading,
        getLegendDialogVisibility,
        getManageDashboardManager,
        getManageHoldsModalVisibility,
        getNotifications,
        getOrderHeaders,
        getOrderInfo,
        getOrderPriceInfo,
        getPasManager,
        getPaymentInstruction,
        getPDF,
        getPDFManager,
        getPrintDialogVisibility,
        getPrintOptionValue,
        getSeatInfoFromIds,
        getSeatmapManager,
        getSeatmapViewBox,
        getSectionHoldsInfo,
        getSoldSeatInfo,
        getSoldSeatInfoFetched,
        getHoverSectionInfo,
        getSectionTotalUnitsInfo,
        getGASectionVariants,
        getSectionTiersData,
        getServerProperty,
        getSyncCartStatus,
        getSuccessMessage,
        getTargetPositionInNormalizedCoordinates,
        getTicketSelection,
        getTiers,
        getTotalUnitsInfo,
        getViewMode,
        getWorldToPixelsFactor,
        getZoomButtons,
        gotoSection,
        handleSelectGATickets,
        hasItemsCarted,
        hasSelectedGATickets,
        hideLegendDialog,
        hidePrintDialog,
        idsBelongToGASection,
        isErrorCode,
        isSeatLocked,
        off,
        on,
        pickSeats,
        redo,
        releaseHold,
        removeCartItem,
        resetSelectedGATickets,
        savePaymentInstruction,
        saveSectionHolds,
        seatmapGetViewport,
        seatmapMouseDown,
        seatmapMouseMove,
        seatmapMouseUp,
        seatmapSetViewport,
        seatmapWheel,
        selectSeats,
        setAddonQuantity,
        setOrderSummaryTicketPrice,
        setCheckoutModalVisibility,
        setCheckoutWidgetVisibility,
        setErrorMessage,
        setManageHoldsModalVisibility,
        setOrderData,
        setPaymentInstruction,
        setPrintOptionValue,
        setSectionHoldQuantity,
        setSelectedGASectionVariantQuantity,
        setSuccessMessage,
        setView,
        setViewMode,
        showManageHoldsModal,
        toggleLegendDialogVisibility,
        togglePrintDialogVisibility,
        undo,
    };
};

export default eventManagerFactory;
